import { useCallback, useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { Archive, Download, RotateCcw, ShieldAlert } from "lucide-react";

export default function GlCleanupPage() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const load = useCallback(() => {
    api.get("/gl-maintenance/orphans").then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load"));
  }, []);
  useEffect(() => { load(); }, [load]);

  async function exportExcel() {
    setBusy(true);
    try {
      const r = await api.get("/gl-maintenance/orphans/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `orphan_gl_entries_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  }

  async function archive() {
    setBusy(true);
    try {
      const r = await api.post("/gl-maintenance/orphans/archive", { confirm: true });
      toast.success(`${r.data.archived} entries archived (batch ${r.data.archive_batch_id.slice(0, 8)}…)`);
      setConfirmText("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Archive failed"); }
    finally { setBusy(false); }
  }

  async function restore(batchId) {
    setBusy(true);
    try {
      const r = await api.post("/gl-maintenance/orphans/restore", { archive_batch_id: batchId });
      toast.success(`${r.data.restored} entries restored`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed"); }
    finally { setBusy(false); }
  }

  if (!data) return <div className="p-8 text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="gl-cleanup-page">
      <PageHeader title="GL Cleanup — Orphan Entries"
        subtitle="One-time Accounting Maintenance utility: GL rows whose source documents were deleted (legacy/demo data). Export for review, then archive — fully restorable." />

      <div className="grid grid-cols-4 gap-2 max-w-3xl">
        {[["Orphan entries", data.entries], ["Voucher groups", data.voucher_groups],
          ["Debit", formatINR(data.debit)], ["Net difference", formatINR(data.net_difference)]].map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{k}</div>
            <div className="text-lg font-black tabular-nums text-slate-900" data-testid={`gl-cleanup-${k.toLowerCase().replace(" ", "-")}`}>{v}</div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200">
        <div className="px-4 py-2.5 bg-[#0B1F3A] text-[10px] uppercase tracking-[0.2em] font-bold text-white">By Voucher Type</div>
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-50 text-slate-500">
              {["Ref Type", "Entries", "Groups", "Debit", "Credit"].map((h, i) => (
                <th key={h} className={`px-4 py-2 text-[10px] uppercase tracking-wider font-bold ${i ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(data.by_ref_type || []).map((r) => (
              <tr key={r.ref_type} className="border-t border-slate-100">
                <td className="px-4 py-1.5 font-semibold text-slate-800">{r.ref_type}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{r.entries}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{r.voucher_groups}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{formatINR(r.debit)}</td>
                <td className="px-4 py-1.5 text-right tabular-nums">{formatINR(r.credit)}</td>
              </tr>
            ))}
            {!(data.by_ref_type || []).length && (
              <tr><td colSpan={5} className="px-4 py-4 text-center text-emerald-700 font-bold">No orphan entries — books are clean</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button data-testid="gl-cleanup-export" onClick={exportExcel} disabled={busy}
          className="border border-slate-300 text-slate-700 px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:border-[#F97316] hover:text-[#F97316] disabled:opacity-50">
          <Download size={12} /> Export for Review
        </button>
        {data.entries > 0 && (
          <>
            <input data-testid="gl-cleanup-confirm-input" value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)} placeholder='Type ARCHIVE to enable'
              className="border border-slate-300 px-3 py-2 text-xs w-44" />
            <button data-testid="gl-cleanup-archive" onClick={archive}
              disabled={busy || confirmText !== "ARCHIVE"}
              className="bg-rose-600 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 hover:bg-rose-700 disabled:opacity-50">
              <Archive size={12} /> Archive {data.entries} Entries
            </button>
          </>
        )}
      </div>
      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 max-w-2xl flex items-start gap-2">
        <ShieldAlert size={13} className="mt-0.5 shrink-0" />
        Archiving moves the rows to a restorable archive (never deleted). Run and review the
        export first. The Opening Balance Wizard assumes the books are clean before posting.
      </p>

      {(data.archives || []).length > 0 && (
        <div className="bg-white border border-slate-200" data-testid="gl-cleanup-archives">
          <div className="px-4 py-2.5 border-b border-slate-100 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Archive Batches (restorable)</div>
          {(data.archives || []).map((a) => (
            <div key={a.archive_batch_id} className="px-4 py-2 border-t border-slate-50 flex items-center gap-3 text-xs">
              <span className="font-mono text-slate-500">{a.archive_batch_id.slice(0, 8)}…</span>
              <span className="font-bold">{a.entries} entries</span>
              <span className="text-slate-500">{String(a.archived_at || "").slice(0, 16).replace("T", " ")} · {a.archived_by}</span>
              <button data-testid={`gl-cleanup-restore-${a.archive_batch_id.slice(0, 8)}`}
                onClick={() => restore(a.archive_batch_id)} disabled={busy}
                className="ml-auto border border-slate-300 px-2.5 py-1 text-[10px] uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50 flex items-center gap-1 disabled:opacity-50">
                <RotateCcw size={10} /> Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
